// src/data/memberTypes.js
//
// Member types are the self-selected "relationship lens" axis of the funnel
// (see docs/eri-hub-member-funnel-spec.md §1–2) — orthogonal to `role` (email-
// derived) and `ageTier` (age-derived). The persona step of the join flow lets
// a user pick one; it is stored on the user doc as `memberType`.
//
// The catalogue is org-configurable: it lives on the org doc as
// `organizations/{orgId}.memberTypes`, right next to `groups` / `features`,
// and is edited per-org in the super-admin Organization editor
// (OrganizationManager.jsx). Each client relabels the same three slots
// without a code change:
//   - NHLAA            -> Former Player / Spouse / Son or Daughter
//   - Alberta Blue Cross -> Employee / Spouse / Dependent
//   - Hockey Alberta   -> Player (expects: any) / Parent
//
// The default below is deliberately NEUTRAL — an unconfigured org must never
// show vertical-specific copy (e.g. "Former Player") to new signups.
//
// TAG vs SURFACE: the three ids are TAGS only — fixed slots used for
// segmentation/reporting, never editable. What ROUTES is the `surface` field:
// resolveSurface.js reads it (via App.jsx) to pick the member's home view.
// The meaningful choice sits on the second-adult ('spouse') slot — e.g.
// NHL Alumni -> 'family' (a partner really is a caregiver), Alberta Blue
// Cross -> 'athlete' (a spouse is just another member on the benefit),
// Hockey Alberta "Parent" -> 'athlete'. The 'child' slot is always age-gated
// (age decides kidz/teen/adult), so its surface isn't a real choice.
//
// `expects` is the age tier a persona normally implies; the join flow's
// gate_confirm step cross-checks the derived ageTier against it ('any' opts
// out of the check). Values: 'kid' | 'teen' | 'adult' | 'any'.
//
// This mirrors the editions pattern (editions/index.js): a sensible default
// plus a resolver that falls back when the org hasn't been configured yet, so
// nothing breaks mid-migration.

export const DEFAULT_MEMBER_TYPES = [
  { id: 'athlete', label: 'Member',           expects: 'adult', surface: 'athlete' },
  { id: 'spouse',  label: 'Spouse / Partner', expects: 'adult', surface: 'family'  },
  { id: 'child',   label: 'Child',            expects: 'any',   surface: 'child'   },
];

// The surfaces a member-type slot can route to. Documentation of the choices —
// the OrganizationManager select and resolveSurface() are the consumers.
export const MEMBER_SURFACES = {
  athlete: 'Adult member surface (their own dashboard)',
  family:  'Family / caregiver hub',
  parent:  'Parent hub (youth sport): a check-in about their player, talk guides',
  child:   'Age-gated (kids / teen) — age decides',
};

// Resolve the member-type catalogue for an org doc. Falls back to the default
// when the org has no `memberTypes` (absent, empty, or not an array), same
// philosophy as DEFAULT_EDITION in editions/index.js.
export function resolveMemberTypes(org) {
  const types = org && org.memberTypes;
  return Array.isArray(types) && types.length > 0 ? types : DEFAULT_MEMBER_TYPES;
}

export const hasConfiguredMemberTypes = (org) =>
  Array.isArray(org && org.memberTypes) && org.memberTypes.length > 0;

// The member personas a super-admin can preview for this org, in the org's
// own words: Hockey Alberta offers Player / Parent / Player 13–17 / Player
// under 13, NHL Alumni offers Former Player / Spouse / Son or Daughter...
// The minor personas use the child slot when the org has one, else the
// athlete slot when it accepts any age (a youth-sport org), else none.
// `memberType` is the slot the persona impersonates, so the preview routes
// exactly as a real member of that org would.
export function previewPersonasFor(org, fallbackLabels = {}) {
  const configured = hasConfiguredMemberTypes(org);
  const types = resolveMemberTypes(org);
  const slot = (id) => types.find((t) => t.id === id) || null;
  const athlete = slot('athlete');
  const spouse = slot('spouse');
  const child = slot('child');
  const label = (s, fallback) => (configured && s && s.label ? s.label : fallback);
  const minorSlot = child || (athlete && athlete.expects === 'any' ? athlete : null);
  const minorLabel = configured && minorSlot ? minorSlot.label : null;
  const out = [{ id: 'athlete', memberType: 'athlete', label: label(athlete, fallbackLabels.athlete || 'Member') }];
  if (!configured || spouse) out.push({ id: 'spouse', memberType: 'spouse', label: label(spouse, fallbackLabels.spouse || 'Spouse') });
  if (!configured || minorSlot) {
    const mt = child ? 'child' : 'athlete';
    out.push({ id: 'teen', memberType: mt, label: minorLabel ? `${minorLabel} 13–17` : (fallbackLabels.teen || 'Teen') });
    out.push({ id: 'kidz', memberType: mt, label: minorLabel ? `${minorLabel} <13` : (fallbackLabels.kidz || 'Kidz') });
  }
  return out;
}
